import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { ProjectPhase, CreateProjectPhase, UpdateProjectPhase } from '@estimator/shared';

export function useProjectPhases(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-phases', projectId],
    queryFn: async () => {
      const { data } = await apiClient.get<ProjectPhase[]>('/project-phases', {
        params: { project_id: projectId },
      });
      return data;
    },
    enabled: !!projectId,
  });
}

export function useCreateProjectPhase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateProjectPhase) => {
      const { data } = await apiClient.post<ProjectPhase>('/project-phases', body);
      return data;
    },
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({ queryKey: ['project-phases', vars.project_id] }),
  });
}

export function useUpdateProjectPhase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateProjectPhase & { id: string }) => {
      const { data } = await apiClient.patch<ProjectPhase>(`/project-phases/${id}`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-phases'] }),
  });
}

export function useDeleteProjectPhase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/project-phases/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-phases'] }),
  });
}

export function useReorderProjectPhases() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await apiClient.patch('/project-phases/reorder', { ids });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-phases'] }),
  });
}
